/** Cursor Cloud Agents API v1 (called via same-origin proxy — see docs/cursor-agent.md). */

import {
  formatCursorFetchError,
  isCursorAgentActiveRunError,
} from '../utils/cursorFetchError';
import { cursorAgentLog } from '../utils/cursorAgentDebug';
import { loadCursorAgentPreferences } from '../utils/cursorAgentPreferences';
import { resolveCursorAgentUrl } from '../utils/cursorAgentUrls';

const API_BASE = '/api/cursor-agent';
const POLL_MS = 2000;
const MAX_POLLS = 90;

export type CursorAgentProgress = {
  phase: 'create' | 'poll' | 'fetch-agent';
  status?: string | undefined;
  poll?: number | undefined;
  agentId?: string | undefined;
  runId?: string | undefined;
};

export type CursorAgentProgressCallback = (progress: CursorAgentProgress) => void;

export function cursorAuthHeader(apiKey: string): string {
  return `Basic ${btoa(`${apiKey}:`)}`;
}

async function apiFetch(apiKey: string, path: string, init: RequestInit = {}): Promise<unknown> {
  const url = `${API_BASE}${path}`;
  cursorAgentLog('fetch', { method: init.method ?? 'GET', url });
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers: {
        Authorization: cursorAuthHeader(apiKey),
        'Content-Type': 'application/json',
        ...(init.headers as Record<string, string> | undefined),
      },
    });
  } catch (err) {
    throw new Error(formatCursorFetchError(err));
  }
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = { raw: text };
  }
  cursorAgentLog('response', { url, status: res.status, body });
  if (!res.ok) {
    const err = body as { error?: { message?: string }; message?: string };
    const msg =
      err?.error?.message ??
      err?.message ??
      (typeof err === 'object' && err && 'error' in err ? String(err.error) : null) ??
      text ??
      res.statusText;
    if (res.status === 409) {
      throw new Error(
        typeof msg === 'string' && msg.trim()
          ? msg
          : 'Agent is still working on the previous message. Wait a moment and try again.'
      );
    }
    throw new Error(typeof msg === 'string' ? msg : `Cursor API ${res.status}`);
  }
  return body;
}

/** Whether a run status from the Cursor API indicates polling can stop. */
export function isTerminalRunStatus(status: unknown): boolean {
  if (!status || typeof status !== 'string') return false;
  const s = status.toLowerCase();
  if (
    s === 'finished' ||
    s === 'error' ||
    s === 'cancelled' ||
    s === 'canceled' ||
    s === 'expired' ||
    s === 'failed' ||
    s === 'done' ||
    s === 'succeeded' ||
    s === 'success'
  ) {
    return true;
  }
  return (
    s.includes('complete') ||
    s.includes('fail') ||
    s.includes('cancel') ||
    (s.includes('error') && s !== 'creating')
  );
}

/** Pull assistant text from a terminal run (or null if not ready / no text). */
export function extractCursorAgentReply(
  run: Record<string, unknown>,
  agent: Record<string, unknown>
): string | null {
  const r = run as {
    result?: string | { text?: string; message?: string };
    output?: { text?: string } | string;
    summary?: string;
    response?: string;
    status?: string;
    error?: { message?: string } | string;
  };

  if (typeof r.result === 'string' && r.result.trim()) return r.result.trim();
  if (r.result && typeof r.result === 'object') {
    const nested = r.result.text ?? r.result.message;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }

  const candidates = [
    typeof r.output === 'object' ? r.output?.text : r.output,
    r.summary,
    r.response,
    (agent as { summary?: string }).summary,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }

  const status = r.status?.toLowerCase() ?? '';
  if (status === 'error' || status === 'failed' || status.includes('fail')) {
    throw new Error(
      typeof r.error === 'object' ? r.error?.message : String(r.error ?? 'Agent run failed')
    );
  }
  if (!isTerminalRunStatus(r.status)) return null;
  return null;
}

async function waitForRun(
  apiKey: string,
  agentId: string,
  runId: string,
  onProgress?: CursorAgentProgressCallback
): Promise<Record<string, unknown>> {
  for (let i = 0; i < MAX_POLLS; i++) {
    const run = (await apiFetch(apiKey, `/v1/agents/${agentId}/runs/${runId}`)) as Record<
      string,
      unknown
    >;
    const status = typeof run.status === 'string' ? run.status : undefined;
    onProgress?.({ phase: 'poll', status, poll: i + 1, agentId, runId });
    if (isTerminalRunStatus(run.status)) return run;
    await new Promise(r => setTimeout(r, POLL_MS));
  }
  throw new Error('AI agent timed out (still running). Try again in a minute.');
}

async function createRun(
  apiKey: string,
  agentId: string,
  message: string,
  onProgress?: CursorAgentProgressCallback
): Promise<Record<string, unknown>> {
  onProgress?.({ phase: 'create', agentId });
  const body = (await apiFetch(apiKey, `/v1/agents/${agentId}/runs`, {
    method: 'POST',
    body: JSON.stringify({ prompt: { text: message } }),
  })) as { run?: { id?: string }; id?: string };
  const runId = body.run?.id ?? body.id;
  if (!runId) throw new Error('Cursor API did not return a run id');
  return waitForRun(apiKey, agentId, runId, onProgress);
}

export type CursorAgentChatResult = {
  agentId: string;
  reply: string;
  runId?: string;
  status?: string;
  agentUrl?: string;
  durationMs?: number;
};

function readDurationMs(run: Record<string, unknown>): number | undefined {
  const ms = run.durationMs;
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : undefined;
}

function readAgentUrl(agent: Record<string, unknown>, agentId: string): string {
  const fromAgent = typeof agent.url === 'string' ? agent.url : undefined;
  return resolveCursorAgentUrl(agentId, fromAgent);
}

function chatResult(
  agentId: string,
  reply: string,
  run: Record<string, unknown>,
  agent: Record<string, unknown>
): CursorAgentChatResult {
  const out: CursorAgentChatResult = {
    agentId,
    reply,
    agentUrl: readAgentUrl(agent, agentId),
  };
  if (typeof run.id === 'string') out.runId = run.id;
  if (typeof run.status === 'string') out.status = run.status;
  const durationMs = readDurationMs(run);
  if (durationMs != null) out.durationMs = durationMs;
  return out;
}

/** Optional repo URL from Tools → Config for agents that clone a GitHub repo. */
export function loadCursorAgentRepoUrl(): string {
  return loadCursorAgentPreferences().repoUrl.trim();
}

/** Poll the agent's latest run after reload (or when a run is still in progress). */
export async function resumeCursorAgentRun(
  apiKey: string,
  agentId: string,
  onProgress?: CursorAgentProgressCallback
): Promise<CursorAgentChatResult | null> {
  const key = apiKey.trim();
  const id = agentId.trim();
  if (!key || !id) return null;

  onProgress?.({ phase: 'fetch-agent', agentId: id });
  const agent = (await apiFetch(key, `/v1/agents/${id}`)) as Record<string, unknown>;
  const runId =
    typeof agent.latestRunId === 'string'
      ? agent.latestRunId
      : typeof (agent as { latest_run_id?: string }).latest_run_id === 'string'
        ? (agent as { latest_run_id: string }).latest_run_id
        : undefined;
  if (!runId) return null;

  let run = (await apiFetch(key, `/v1/agents/${id}/runs/${runId}`)) as Record<string, unknown>;
  const initialStatus = typeof run.status === 'string' ? run.status : undefined;
  onProgress?.({ phase: 'poll', status: initialStatus, poll: 1, agentId: id, runId });

  if (!isTerminalRunStatus(run.status)) {
    run = await waitForRun(key, id, runId, onProgress);
  }

  const reply = extractCursorAgentReply(run, agent);
  if (!reply) {
    const status = typeof run.status === 'string' ? run.status : 'unknown';
    if (!isTerminalRunStatus(run.status)) return null;
    return chatResult(
      id,
      `The agent finished (${status}) but returned no text. Try a clearer prompt or check the run in the Cursor dashboard.`,
      run,
      agent
    );
  }

  return chatResult(id, reply, run, agent);
}

export type CursorAgentChatOptions = {
  /** When false, do not attach GitHub repo from Config (faster for News ranking). Default true. */
  attachRepo?: boolean;
};

export async function chatWithCursorAgent(
  apiKey: string,
  message: string,
  agentId: string | null,
  onProgress?: CursorAgentProgressCallback,
  options: CursorAgentChatOptions = {}
): Promise<CursorAgentChatResult> {
  const key = apiKey.trim();
  if (!key) throw new Error('Add your Cursor API key in Tools → Config.');

  let resolvedAgentId = agentId?.trim() ?? '';
  let run: Record<string, unknown>;
  let agent: Record<string, unknown> = {};

  if (resolvedAgentId) {
    try {
      run = await createRun(key, resolvedAgentId, message, onProgress);
    } catch (err) {
      if (isCursorAgentActiveRunError(err)) {
        const resumed = await resumeCursorAgentRun(key, resolvedAgentId, onProgress);
        if (resumed) return resumed;
      }
      throw err;
    }
    onProgress?.({ phase: 'fetch-agent', agentId: resolvedAgentId });
    agent = (await apiFetch(key, `/v1/agents/${resolvedAgentId}`)) as Record<string, unknown>;
  } else {
    onProgress?.({ phase: 'create' });
    const payload: Record<string, unknown> = {
      prompt: { text: message },
      model: { id: 'composer-2.5' },
    };
    const attachRepo = options.attachRepo !== false;
    if (attachRepo) {
      const repo = loadCursorAgentRepoUrl();
      if (repo) {
        payload.repos = [{ url: repo, startingRef: 'main' }];
      }
    }
    const body = (await apiFetch(key, '/v1/agents', {
      method: 'POST',
      body: JSON.stringify(payload),
    })) as {
      agent?: { id?: string };
      id?: string;
      run?: { id?: string };
      latestRunId?: string;
    };
    resolvedAgentId = body.agent?.id ?? body.id ?? '';
    if (!resolvedAgentId) throw new Error('Cursor API did not return an agent id');
    const runId = body.run?.id ?? body.latestRunId;
    agent = (body.agent ?? body) as Record<string, unknown>;
    run = runId
      ? await waitForRun(key, resolvedAgentId, runId, onProgress)
      : await createRun(key, resolvedAgentId, message, onProgress);
  }

  const reply = extractCursorAgentReply(run, agent);
  if (!reply) {
    const status = typeof run.status === 'string' ? run.status : 'unknown';
    cursorAgentLog('no reply text', { status, run, agent });
    return chatResult(
      resolvedAgentId,
      `The agent finished (${status}) but returned no text. Try a clearer prompt or check the run in the Cursor dashboard.`,
      run,
      agent
    );
  }

  return chatResult(resolvedAgentId, reply, run, agent);
}

/** Quick connectivity check (proxy + API key). */
export async function testCursorAgentApiKey(apiKey: string): Promise<void> {
  const key = apiKey.trim();
  if (!key) throw new Error('Enter a Cursor API key first.');
  await apiFetch(key, '/v1/agents?limit=1', { method: 'GET' });
}
